import type { RequestOpts } from '@oazapfts/runtime';
import { Err, Ok } from 'ts-results-es';

import { appConfig } from '@/config';
import type { Auth } from '@/entities/auth';
import type { Me } from '@/entities/me';

import { FeApi } from '..';
import type { ISiteRepository } from '../domain/repositories/site.repository';
import type { RequestOptions } from '../domain/repositories/utils/request-options';
import { handle } from '../helpers/handle';
import { SiteRepositoryMapper } from '../mappers/site.repository.mapper';

export class FeApiSiteRepository implements ISiteRepository {
  constructor(
    private me: Me,
    private auth: Auth,
  ) {}

  private async getOpts(): Promise<RequestOpts> {
    const accessToken = await this.auth.getAccessToken();

    return {
      baseUrl: `${appConfig.bffUrl}/plex`,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-user-email': this.me.email,
        'x-user-name': this.me.displayName,
      },
    };
  }

  async getAll(
    requestOptions?: RequestOptions,
  ): Promise<ISiteRepository.GetAllOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.customerInfosV1SitesListBasicRetrieve(
      {},
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ISiteRepository.GetAllOutput>({
      async 200() {
        const sites = await SiteRepositoryMapper.getAll.outputFromData(
          response.data,
        );

        return sites;
      },
    });
  }
  async getAllExtended(
    requestOptions?: RequestOptions,
  ): Promise<ISiteRepository.GetAllExtendedOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.customerInfosV1SitesListRetrieve(
      {},
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ISiteRepository.GetAllExtendedOutput>({
      async 200() {
        const sites = await SiteRepositoryMapper.getAllExtended.outputFromData(
          response.data,
        );

        return sites;
      },
    });
  }

  async getById(
    input: ISiteRepository.GetByIdInput,
    requestOptions?: RequestOptions,
  ): Promise<ISiteRepository.GetByIdOutput> {
    if (input.id === undefined) {
      return Err({ type: 'NOT_FOUND' });
    }

    const opts = await this.getOpts();
    const body = SiteRepositoryMapper.getById.bodyFromInput(input);
    const response = await FeApi.customerInfosV2SitesDetailRetrieve(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<ISiteRepository.GetByIdOutput>({
      async 200(data) {
        const site = await SiteRepositoryMapper.getById.outputFromData(data);

        return Ok(site);
      },
      404() {
        return Err({ type: 'NOT_FOUND' });
      },
    });
  }

  async getLevels(
    input: ISiteRepository.GetLevelsInput,
    requestOptions?: RequestOptions,
  ): Promise<ISiteRepository.GetLevelsOutput> {
    const opts = await this.getOpts();
    const response = await FeApi.customerInfosV1CustomerLevelsRetrieve(
      { siteAccureId: input.siteId },
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<ISiteRepository.GetLevelsOutput>({
      async 200(data) {
        return Ok(data.levels);
      },
      400() {
        return Err({ type: 'NOT_FOUND' });
      },
      422({ error }) {
        return Err({ type: 'EXTERNAL_ERROR', description: error });
      },
    });
  }

  async update(
    input: ISiteRepository.UpdateInput,
    requestOptions?: RequestOptions,
  ): Promise<ISiteRepository.UpdateOutput> {
    const opts = await this.getOpts();
    const body = SiteRepositoryMapper.update.bodyFromInput(input);
    const response = await FeApi.customerInfosV1SitesUpdateUpdate(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<ISiteRepository.UpdateOutput>({
      200() {
        return Ok(undefined);
      },
      422({ error }) {
        return Err({ type: 'EXTERNAL_ERROR', description: error });
      },
    });
  }

  async create(
    input: ISiteRepository.CreateInput,
    requestOptions?: RequestOptions,
  ): Promise<ISiteRepository.CreateOutput> {
    const opts = await this.getOpts();
    const body = SiteRepositoryMapper.create.bodyFromInput(input);
    const response = await FeApi.customerInfosV1SitesNewCreate(body, {
      ...opts,
      signal: requestOptions?.signal,
    });

    return handle(response).to<ISiteRepository.CreateOutput>({
      201(data) {
        return Ok({ id: data.accure_id });
      },
      422({ error }) {
        return Err({ type: 'EXTERNAL_ERROR', description: error });
      },
    });
  }
}
